import { getDynamicSetting } from '../utils/dynamicSettings';

export const GIT_PROVIDERS = {
  GITHUB: 'github',
  BITBUCKET: 'bitbucket',
  BITBUCKET_DATACENTER: 'bitbucket_datacenter',
  GITLAB: 'gitlab',
  AZURE: 'azure',
  GERRIT: 'gerrit',
} as const;

export type GitProvider = (typeof GIT_PROVIDERS)[keyof typeof GIT_PROVIDERS];

const KNOWN_GIT_PROVIDERS: ReadonlySet<string> = new Set(
  Object.values(GIT_PROVIDERS),
);

/**
 * Narrows an unknown string to `GitProvider`.
 * Returns `undefined` when the value is not a known provider.
 */
export const toGitProvider = (value: string): GitProvider | undefined =>
  KNOWN_GIT_PROVIDERS.has(value) ? (value as GitProvider) : undefined;

export const GITHUB_SAAS_URL = 'https://github.com/apps/qodo-code-review';

// A getter, not a module-level const: config arrives from an async backend fetch,
// so a value snapshotted at module-eval would freeze on the provisional fallback.
export const getGithubClientId = () =>
  getDynamicSetting('github_client_id_legacy');

/**
 * Ordered array of git providers that can be linked to the user's account
 * Order: GitHub, Bitbucket, GitLab, Azure
 */
export const linkableGitProviders: GitProvider[] = [
  GIT_PROVIDERS.GITHUB,
  GIT_PROVIDERS.GITLAB,
  GIT_PROVIDERS.BITBUCKET,
];

/**
 * Ordered array of git providers that can be linked to the user's account in the Saas tab
 * Order: GitHub, Bitbucket, GitLab, Azure
 * Azure is only available in the Saas tab, so it is not included in the linkableGitProviders array
 */
export const linkableSaaSGitProviders: GitProvider[] = [
  ...linkableGitProviders,
  GIT_PROVIDERS.AZURE,
];

/**
 * Git providers that have no SaaS offering and are connected through an in-app
 * installation wizard rather than an OAuth "link account" flow. They appear
 * only on the Self-Hosted tab (gated by their feature flag) — never in the SaaS
 * tab, the sidebar "link account" menu, or the Home linkable-providers card.
 */
export const selfHostedOnlyGitProviders: GitProvider[] = [GIT_PROVIDERS.GERRIT];
